import React from 'react';
import { Label, Item, Icon, Popup } from 'semantic-ui-react';
// i18n
import { useTranslation } from 'react-i18next';
// tracking
import ReactGA from 'react-ga';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import privacy from '../../config/privacy';
// image handling
import imageUtil from '../../utils/imageUtil';
import ReviewItem from '../review/reviewItem';

import PublishingSettings from '../../containers/book/detail/publishingSettings';
import { Media } from 'src/utils/Media';

export const DEFAULT_COVER_BACKGROUND =
  'https://storage.googleapis.com/beta-reader-prod.appspot.com/defaults/DEFAULT_COVER_BACKGROUND.png';

const ResponsiveImage = styled(Item.Image)`
  @media (max-width: 767px) {
    display: none !important;
  }
  > img {
    border-radius: 10px !important;
  }
`;

const BookItem = ({ book }) => {
  const history = useHistory();
  const { t } = useTranslation();

  const blockClick = event => {
    event.stopPropagation();
  };

  const goToBookSubPage = (event, subPage) => {
    blockClick(event);
    ReactGA.event({
      category: 'General',
      action: `Clicked Book ${subPage}`,
    });
  };

  if (!book) return null;
  return (
    <Item
      key={book._id}
      className="manuscript-book-item"
      style={{ cursor: 'pointer' }}
      onClick={() => {
        history.push(`/books/${book._id}`);
        ReactGA.event({
          category: 'General',
          action: 'Clicked Book Card',
        });
      }}
    >
      <ResponsiveImage
        /* hide when the list of items flips to "vertical view"
          because it causes the image to overtake everything */
        size="tiny"
        src={imageUtil.getImageUrl({
          operation: 'width',
          size: 200,
          originalImageUrl:
            book?.cover?.highRes?.url ?? DEFAULT_COVER_BACKGROUND,
        })}
      />
      <Item.Content>
        <Item.Header style={{ display: 'flex' }}>
          <Popup
            hoverable
            trigger={
              <Icon
                name={privacy.get(book, 'iconName')}
                style={{ alignSelf: 'flex-start' }}
                onClick={blockClick}
              />
            }
            content={<PublishingSettings book={book} />}
          />
          <Link
            className="book-item-title"
            style={{ flex: 1 }}
            to={`/books/${book._id}`}
            onClick={event => {
              blockClick(event);
              ReactGA.event({
                category: 'General',
                action: 'Clicked Book Card',
              });
            }}
          >
            {book.title}
          </Link>
          {book.referenceId !== undefined && (
            <span
              className="book-item-reference"
              style={{ fontSize: '0.8em', fontWeight: 'normal' }}
            >
              {` (${book.referenceId})`}
            </span>
          )}
          {book.role === 'collaborator' && (
            <Label
              size="mini"
              basic
              content={t('collaborator')}
              style={{ marginLeft: '1em' }}
            />
          )}
        </Item.Header>
        <Item.Meta>
          {t('by')} {book.authorName}{' '}
          <span className="book-item-length">
            ({t('ChapterCount', { count: book.chapterCount || 0 })},{' '}
            {t('WordCountString', { count: book.wordCount || 0 })})
          </span>
        </Item.Meta>
        <Item.Extra>
          {book.description && book.description.length > 250
            ? `${book.description.substring(0, 250)}...`
            : book.description}
        </Item.Extra>
        <Item.Extra className="book-item-metrics">
          <Label
            as={Link}
            to={`/books/${book._id}/feedback/comments`}
            aria-label={`${t('Comments')}: ${book.totalComments || 0}`}
            title={t('Comments')}
            basic
            color="teal"
            onClick={event => goToBookSubPage(event, 'feedback/comments')}
          >
            <Icon name="comment outline" />
            {book.totalComments || 0}
          </Label>
          <Label
            as={Link}
            to={`/books/${book._id}/followers`}
            aria-label={`${t('Followers')}: ${book.totalFollowers || 0}`}
            title={t('Followers')}
            basic
            color="teal"
            onClick={event => goToBookSubPage(event, 'followers')}
          >
            <Icon name="feed" />
            {book.totalFollowers || 0}
          </Label>
          <Label
            as={Link}
            to={`/books/${book._id}/feedback/reviews`}
            aria-label={`${t('Reviews')}: ${book.totalReviews || 0}`}
            title={t('Reviews')}
            basic
            color="teal"
            onClick={event => goToBookSubPage(event, 'feedback/reviews')}
          >
            <Icon name="eye" />
            {book.totalReviews || 0}
          </Label>
        </Item.Extra>
        <Media at="mobile">
          {(className, renderChildren) =>
            renderChildren && book.avgOverAllRating ? (
              <Label basic size="mini" className="book-item-rating">
                <Icon name="star outline" />
                {book.avgOverAllRating.toFixed(1)}
              </Label>
            ) : null
          }
        </Media>
      </Item.Content>
      <Media greaterThan="mobile" className="book-item-reviews">
        <ReviewItem reviewData={book} t={t} />
      </Media>
    </Item>
  );
};

export default BookItem;
